import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../contexts/ToastContext';
import logo from '../logo.svg';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !password) {
      showError('Please enter both email and password.');
      return;
    }
    
    if (!email.includes('@')) {
      showError('Please enter a valid email address.');
      return;
    }
    
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        showError(result.message || 'Invalid email or password. Please check your credentials and try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Provide specific error messages based on error type
      if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
        showError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (err.response?.status === 429) {
        showError('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (err.response?.status === 401) {
        showError('Invalid email or password. Please check your credentials.');
      } else if (err.response?.status >= 500) {
        showError('Server error. Please try again later or contact support if the problem persists.');
      } else {
        showError('Login failed. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Background with healthcare/CPR theme */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-red-100"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(255, 25, 25, 0.1) 0%, rgba(255, 255, 255, 0.9) 50%, rgba(255, 25, 25, 0.05) 100%), 
                           url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><defs><pattern id="medical" patternUnits="userSpaceOnUse" width="100" height="100"><circle cx="50" cy="50" r="2" fill="%23FF1919" opacity="0.1"/></pattern></defs><rect width="100%25" height="100%25" fill="url(%23medical)"/></svg>')`
        }}
      />
      
      {/* Floating medical icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-8 h-8 text-red-200 animate-pulse">
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 2a8 8 0 100 16 8 8 0 000-16zM9 9V6a1 1 0 112 0v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="absolute top-40 right-20 w-6 h-6 text-red-100 animate-pulse" style={{animationDelay: '1s'}}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="absolute bottom-32 left-20 w-10 h-10 text-red-200 animate-pulse" style={{animationDelay: '2s'}}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg transform transition-all duration-300 hover:scale-105 card-hover p-2 logo-container animate-fade-in" style={{border: '3px solid #FF0404'}}>
            <img 
              src={"https://premierchoicecpr.com/wp-content/uploads/2022/02/cropped-Premier_Choice_CPR.cdr_1.cdr_final-removebg-preview.png"} 
              alt="Premier Choice CPR Logo" 
              className="w-full h-full object-contain logo-image"
              style={{filter: 'drop-shadow(0 2px 4px rgba(255,4,4,0.3))'}} 
            />
          </div>
        </div>
        <h2 className="mt-6 text-center text-4xl font-extrabold text-gray-900">
          Premier Choice CRM
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 font-medium">
          Healthcare Certification Management
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10 animate-slide-up">
        <div className="bg-white/95 backdrop-blur-sm py-8 px-4 shadow-2xl rounded-2xl sm:px-10 border border-white/20 card-hover">
          <form className="space-y-6" onSubmit={handleSubmit}>

            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-brand-primary focus:border-brand-primary sm:text-sm transition-all duration-200 hover:border-gray-400"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-brand-primary focus:border-brand-primary sm:text-sm transition-all duration-200 hover:border-gray-400"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary btn-animate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-brand-primary hover:text-red-700 font-medium transition-colors duration-200"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-600">
            Sub Admin?{' '}
            <Link 
              to="/sub-admin/login" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sub Admin Login
            </Link>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Login;